"use client";

import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  CloudCheck,
  CloudOff,
  Eye,
  EyeOff,
  GripVertical,
  LockKeyhole,
  TriangleAlert,
  Zap,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Theme_logo } from "@/lib/Images";
import { initAlertMsg } from "@/lib/constant/constant";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetAppOnboardingByIdQuery,
  useUpdateAppOnboardingMutation,
} from "@/store/slices/apiSlice";
import AlertMsg from "@/components/dialogs/common/AlertMsg";
import { CustomSuccessModal } from "@/components/tenants/CustomSuccessModal";
import { loadPrimaryColor } from "@/store/slices";

const getMaxLength = (field) => (field?.kind === "button" ? 20 : 225);
const isToggleLockedScreen = (step) => {
  return step?.id === "splash_screen" || step?.id === "home_screen";
};
const DEFAULT_TEMPLATE_BRAND_COLORS = [
  "#00d3f2",
  "#67d8ff",
  "#12d9ff",
  "#00d7f2",
  "#7edbff",
];

const normalizeColor = (color = "") => {
  return color?.trim?.().toLowerCase();
};

const isTemplateBrandColor = (color = "") => {
  return DEFAULT_TEMPLATE_BRAND_COLORS.includes(normalizeColor(color));
};

const resolveInitialButtonBGColor = (savedButtonBGColor, primaryColor) => {
  const savedColor = normalizeColor(savedButtonBGColor);

  // No saved color means this is the first configuration.
  if (!savedColor) {
    return primaryColor || "#00D3F2";
  }

  // Old template/default color should be replaced by tenant primary color.
  if (isTemplateBrandColor(savedColor)) {
    return primaryColor || "#00D3F2";
  }

  // Admin previously selected a custom button color.
  return savedButtonBGColor;
};

const shouldUsePrimaryColorByDefault = (element) => {
  const label = element?.label?.toLowerCase() || "";
  const key = element?.key?.toLowerCase() || "";

  return (
    element?.elementType !== "BUTTON" &&
    (label.includes("title") ||
      label.includes("heading") ||
      label.includes("highlight") ||
      key.includes("title") ||
      key.includes("heading") ||
      key.includes("highlight"))
  );
};

const mapOnboardingScreenToStep = (
  screen,
  index,
  primaryColor = "#00D3F2",
  savedButtonBGColor = "",
) => {
  return {
    id: screen.key || screen._id || screen.id,
    backendId: screen._id || screen.id || null,
    apiKey: screen.key,
    title: screen.title || `Screen ${index + 1}`,
    step: index + 1,
    priorityOrder: screen.priorityOrder || index + 1,
    type: "Screen",
    isVisible: screen.isVisible !== false,

    editableFields: [...(screen.elements || [])]
      .sort((a, b) => (a.priorityOrder || 0) - (b.priorityOrder || 0))
      .map((element, elementIndex) => {
        const data = element.defaults || {};

        const isPrimaryTextByDefault = shouldUsePrimaryColorByDefault(element);
        //  special case: splash title should never use primary color
        const forceNoPrimary =
          screen.key === "splash_screen" && element.key === "splash_title";

        const savedColor = data.color || "#FFFFFF";

        const isSplashScreen = screen.key === "splash_screen";

        const savedColorMatchesPrimary =
          normalizeColor(savedColor) === normalizeColor(primaryColor);

        const savedColorIsTemplateColor = isTemplateBrandColor(savedColor);

        const usesPrimaryColor = isSplashScreen
          ? false
          : data.usesPrimaryColor === true ||
            (data.usesPrimaryColor !== false &&
              isPrimaryTextByDefault &&
              (savedColorMatchesPrimary || savedColorIsTemplateColor));

        return {
          id: element.key
            ? `${screen.key}__${element.key}`
            : element._id || element.id,

          backendId: element._id || element.id || null,
          key: element.key,
          label: element.label || `Field ${elementIndex + 1}`,
          elementType: element.elementType,
          kind: element.elementType === "BUTTON" ? "button" : "text",

          priorityOrder: element.priorityOrder || elementIndex + 1,

          value: data.text || "",
          color: savedColor,
          usesPrimaryColor,

          fontSize: data.fontSize || 14,

          buttonBGColor:
            element.elementType === "BUTTON"
              ? resolveInitialButtonBGColor(
                  data.buttonBGColor || data.buttonColor,
                  primaryColor,
                )
              : null,

          linkType: data.linkType || "NONE",
          linkValue: data.linkValue || null,
          validations: element.validations || {},
        };
      }),
  };
};
const updateStepsPriorityOrder = (list = []) => {
  return list.map((step, index) => ({
    ...step,
    step: index + 1,
    priorityOrder: index + 1,
    isVisible: step.isVisible !== false,
  }));
};

const getButtonBgColor = (field, primaryColor) => {
  return field?.buttonBGColor || primaryColor || "#00D3F2";
};
const getField = (step, key) => {
  return step?.editableFields?.find((field) => field.key === key);
};

const getTextColor = (field, primaryColor) => {
  if (!field) return "#FFFFFF";

  if (field.usesPrimaryColor === true) {
    return primaryColor || field.color || "#FFFFFF";
  }

  return field.color || "#FFFFFF";
};

export default function OnboardingEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const tenantId = location.state?.tenantId;
  const onboardingId = location.state?.onboardingId;
  const onboarding = location.state?.onboardingData;

  useEffect(() => {
    if (!onboardingId) {
      navigate(
        "/tenants/profile",
        { state: { tenantId } },
        {
          replace: true,
        },
      );
      return;
    }
  }, [onboardingId, navigate]);

  const {
    data: onboardingResponse,
    isLoading: isOnboardingLoading,
    isError: isOnboardingError,
    error: onboardingError,
  } = useGetAppOnboardingByIdQuery(
    { onboardingId },
    {
      skip: !onboardingId,
    },
  );

  const [updateAppOnboarding, { isLoading: isUpdatingOnboarding }] =
    useUpdateAppOnboardingMutation();

  const dispatch = useDispatch();

  const globalPrimaryColor = useSelector((state) => state.global.primaryColor);

  const savedPrimaryColor = localStorage.getItem(`tenant-primary-colors-${tenantId}`);

  const primaryColor = globalPrimaryColor || savedPrimaryColor || "#00D3F2";

  // console.log("primary color =========", primaryColor, "Saved color", savedPrimaryColor);

  const [steps, setSteps] = useState([]);
  const [selectedStepId, setSelectedStepId] = useState("");
  const [selectedFieldId, setSelectedFieldId] = useState("");
  const [onboardingName, setOnboardingName] = useState("");
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [alertMsg, setAlertMsg] = useState(initAlertMsg);
  const [lastSavedStepId, setLastSavedStepId] = useState(null);

  const [isEditorInitialized, setIsEditorInitialized] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  const onboardingDetails = useMemo(() => {
    const data =
      onboardingResponse?.response?.data ||
      onboardingResponse?.data?.response?.data ||
      onboardingResponse?.data ||
      onboardingResponse ||
      null;

    if (Array.isArray(data)) {
      return (
        data.find(
          (item) => item?._id === onboardingId || item?.id === onboardingId,
        ) ||
        data[0] ||
        null
      );
    }

    return data;
  }, [onboardingResponse, onboardingId]);

  const [draggedStepId, setDraggedStepId] = useState("");
  const [isDirty, setIsDirty] = useState(false);

  const selectedStep = useMemo(() => {
    return steps.find((step) => step.id === selectedStepId);
  }, [steps, selectedStepId]);

  const selectedField = useMemo(() => {
    return selectedStep?.editableFields?.find(
      (field) => field.id === selectedFieldId,
    );
  }, [selectedStep, selectedFieldId]);

  useEffect(() => {
    if (!selectedStep || selectedStep.isVisible !== false) return;

    const nextVisibleStep = steps.find((step) => step.isVisible !== false);

    if (!nextVisibleStep) return;

    setSelectedStepId(nextVisibleStep.id);
    setSelectedFieldId(nextVisibleStep.editableFields?.[0]?.id || "");
  }, [steps, selectedStep]);

  const handleAlertClose = () => {
    const isSuccess = !alertMsg.isError;

    setAlertMsg(initAlertMsg);
    setShowSaveConfirm(false);

    if (isSuccess) {
      navigate("/tenants/profile", {
        state: {
          tenantId,
          activeTab: "onboarding",
        },
        replace: true,
      });
    }
  };
  useEffect(() => {
    if (tenantId && onboardingDetails) {
      dispatch(
        loadPrimaryColor({
          tenantId,
          primaryColor: onboardingDetails.primaryColor,
        }),
      );
    }
  }, [tenantId, onboardingDetails, dispatch]);

  useEffect(() => {
    if (!onboardingDetails || isEditorInitialized) return;

    const apiScreens = Array.isArray(onboardingDetails?.screens)
      ? onboardingDetails.screens
      : [];

    const savedButtonBGColor = resolveInitialButtonBGColor(
      onboardingDetails?.buttonBGColor,
      primaryColor,
    );

    const mappedSteps = [...apiScreens]
      .sort((a, b) => (a.priorityOrder || 0) - (b.priorityOrder || 0))
      .map((screen, index) =>
        mapOnboardingScreenToStep(
          screen,
          index,
          primaryColor,
          savedButtonBGColor,
        ),
      );

    const mappedStepsold = [...apiScreens]
      .sort((a, b) => (a.priorityOrder || 0) - (b.priorityOrder || 0))
      .map((screen, index) =>
        mapOnboardingScreenToStep(screen, index, primaryColor),
      );

    setSteps(mappedSteps);
    setOnboardingName(onboardingDetails?.name || "");

    const firstVisibleStep =
      mappedSteps.find((step) => step.isVisible !== false) || mappedSteps[0];

    setSelectedStepId(firstVisibleStep?.id || "");
    setSelectedFieldId(firstVisibleStep?.editableFields?.[0]?.id || "");

    setIsDirty(false);
    setIsEditorInitialized(true);
  }, [onboardingDetails, isEditorInitialized, primaryColor]);

  const isLockedStepByIndex = (index, list = steps) => {
    return index === 0 || index === list.length - 1;
  };

  const handleSelectStep = (stepId) => {
    const step = steps.find((item) => item.id === stepId);

    if (!step || step.isVisible === false) return;

    //  AUTO SAVE CURRENT STEP BEFORE SWITCH
    if (isDirty && selectedStepId) {
      triggerAutoSave();
    }

    setSelectedStepId(step.id);
    setSelectedFieldId(step.editableFields?.[0]?.id || "");
  };

  const updateField = (fieldId, patch) => {
    if (!selectedStepId || !fieldId) return;

    setIsDirty(true);

    setSteps((prev) =>
      prev.map((step) => {
        if (step.id !== selectedStepId) return step;

        return {
          ...step,
          editableFields: step.editableFields.map((field) =>
            field.id === fieldId ? { ...field, ...patch } : field,
          ),
        };
      }),
    );
  };

  const triggerAutoSave = async () => {
    if (!onboardingId) return;

    try {
      const payload = buildOnboardingPayload();

      await updateAppOnboarding({
        onboardingId,
        body: payload,
      }).unwrap();

      setIsDirty(false);
      setLastSavedStepId(selectedStepId);
    } catch (err) {
      console.error("Auto save failed", err);
    }
  };
  const toggleStepVisibility = (stepId) => {
    setSteps((prev) =>
      prev.map((step) => {
        if (step.id !== stepId) return step;

        //  block splash + home screen toggle
        if (isToggleLockedScreen(step)) {
          return step;
        }

        setIsDirty(true);

        return {
          ...step,
          isVisible: step.isVisible === false,
        };
      }),
    );
  };

  const handleDragStart = (stepId, index) => {
    if (isLockedStepByIndex(index)) return;

    setDraggedStepId(stepId);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (targetStepId) => {
    if (!draggedStepId || draggedStepId === targetStepId) {
      setDraggedStepId("");
      return;
    }

    setSteps((prev) => {
      const fromIndex = prev.findIndex((step) => step.id === draggedStepId);
      const toIndex = prev.findIndex((step) => step.id === targetStepId);

      if (fromIndex === -1 || toIndex === -1) return prev;

      const draggedIsLocked = isLockedStepByIndex(fromIndex, prev);
      const targetIsLocked = isLockedStepByIndex(toIndex, prev);

      if (draggedIsLocked || targetIsLocked) {
        return prev;
      }

      const nextSteps = [...prev];
      const [draggedStep] = nextSteps.splice(fromIndex, 1);
      nextSteps.splice(toIndex, 0, draggedStep);

      setIsDirty(true);

      return updateStepsPriorityOrder(nextSteps);
    });

    setDraggedStepId("");
  };

  const buildOnboardingPayload = () => {
    const orderedSteps = updateStepsPriorityOrder(steps);

    return {
      name: onboardingName || onboardingDetails?.name || "",

      description: onboardingDetails?.description || "",

      tenantId: onboardingDetails?.tenantId || tenantId,

      tenantName: onboardingDetails?.tenantName || "",

      screens: orderedSteps.map((step, stepIndex) => ({
        ...(step.backendId ? { _id: step.backendId } : {}),

        key: step.apiKey || step.id,
        title: step.title,
        priorityOrder: stepIndex + 1,
        isVisible: step.isVisible !== false,

        elements: [...(step.editableFields || [])]
          .sort((a, b) => (a.priorityOrder || 0) - (b.priorityOrder || 0))
          .map((field, fieldIndex) => ({
            ...(field.backendId ? { _id: field.backendId } : {}),

            elementType: field.elementType,
            key: field.key,
            priorityOrder: fieldIndex + 1,
            label: field.label,

            defaults: {
              text: field.value || "",

              color:
                field.usesPrimaryColor === true
                  ? primaryColor
                  : field.color || "#FFFFFF",

              usesPrimaryColor: field.usesPrimaryColor === true,

              buttonBGColor:
                field.kind === "button"
                  ? field.buttonBGColor || primaryColor || "#00D3F2"
                  : undefined,

              fontSize: field.fontSize || 14,
              linkType: field.linkType || "NONE",
              linkValue: field.linkValue || null,
            },

            validations: field.validations || {},
          })),
      })),

      isMaster: onboardingDetails?.isMaster ?? false,
      version: onboardingDetails?.version || 1,
      isPublished: onboardingDetails?.isPublished ?? false,
      status: onboardingDetails?.status || "ACTIVE",
    };
  };

  const handleConfirmSave = async () => {
    if (!onboardingId) {
      setAlertMsg({
        open: true,
        label: "Unable to Save",
        msg: "Onboarding ID is missing.",
        isError: true,
        btnText: "Close",
        canClose: false,
      });

      return;
    }

    try {
      const payload = buildOnboardingPayload();

      const res = await updateAppOnboarding({
        onboardingId,
        body: payload,
      }).unwrap();

      setShowSaveConfirm(false);
      setIsDirty(false);

      setAlertMsg({
        open: true,
        label: "Onboarding Updates Saved",
        msg: "Your onboarding updates are now live. Users will now see the latest onboarding screens when they open the app.",
        isError: false,
        btnText: "Close",
        canClose: false,
      });
    } catch (error) {
      setShowSaveConfirm(false);
      setAlertMsg({
        open: true,
        label: "Update Failed",
        msg:
          error?.data?.response?.message ||
          error?.data?.message ||
          error?.message ||
          "Unable to update onboarding. Please try again.",
        isError: true,
        btnText: "Close",
        canClose: false,
      });
    }
  };
  return (
    <div className="min-h-screen bg-[#080D18] text-neutral-100">
      <header className="flex h-12 items-center justify-between border-b border-brand-26 bg-brand-27 px-6">
        <div className="flex items-center gap-4">
          {/* <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            className="h-8 border-[#3C526E] bg-transparent px-4 text-brand-24 hover:bg-white/5 hover:text-brand-24"
          >
            <ChevronLeft className="h-4 w-4" />
            Exit
          </Button> */}

          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (isDirty) {
                setShowExitConfirm(true);
                return;
              }
              setShowExitConfirm(true);

              // navigate(-1);
            }}
            className="h-8 border-[#3C526E] bg-transparent px-4 text-brand-24 hover:bg-white/5 hover:text-brand-24"
          >
            <ChevronLeft className="h-4 w-4" />
            Exit
          </Button>

          {/* <div className="flex h-8 items-center gap-3 rounded border border-[#3C526E] bg-[#0D1423] px-4">
            <Input
              value={onboardingName}
              onChange={(event) => {
                setOnboardingName(event.target.value);
                setIsDirty(true);
              }}
              className="h-6 w-[190px] border-none bg-transparent p-0 text-base font-semibold text-neutral-100 outline-none focus-visible:ring-0"
            />
          </div> */}
          <div className="flex h-8 items-center gap-2 rounded border border-[#3C526E] bg-[#0D1423] px-4">
            {/* Tenant Name (read-only) */}
            <span className="text-sm font-semibold text-neutral-100">
              {onboardingDetails?.tenantName || ""}
            </span>

            <span className="text-sm font-semibold text-neutral-100">:</span>

            {/* Onboarding Name (read-only) */}
            <span className="text-sm font-semibold text-neutral-100">
              {onboardingName || ""}
            </span>
          </div>
        </div>

        <div className="rounded-xl border border-[#2D3650] bg-[#111827] px-6 py-2 text-sm font-medium">
          Mobile
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-neutral-100/50">
            {isDirty ? (
              <>
                <CloudOff className="h-4 w-4 text-neutral-100/50" />
                <span>Unsaved Changes</span>
              </>
            ) : (
              <>
                <CloudCheck className="h-4 w-4 text-neutral-100/50" />
                <span>Saved</span>
              </>
            )}
          </div>

          <Button
            type="button"
            onClick={() => setShowSaveConfirm(true)}
            // disabled={isUpdatingOnboarding || !isDirty}
            className="h-8 bg-brand-28 px-5 text-sm font-bold text-black hover:bg-[#00BBD7] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Save
          </Button>
        </div>
      </header>

      <div className="grid h-[calc(100vh-48px)] grid-cols-[320px_1fr_320px]">
        <aside className="overflow-y-auto border-r border-brand-26 bg-brand-27 px-4 py-5">
          <div className="mb-5">
            <h2 className="text-sm font-semibold text-neutral-100">
              Onboarding Flow
            </h2>
            <p className="mt-1 text-xs text-neutral-100/45">
              {steps.length} screens in sequence
            </p>
          </div>

          <div className="space-y-2">
            {steps.map((step, index) => {
              const isFirst = index === 0;
              const isLast = index === steps.length - 1;
              const isLocked = isFirst || isLast;
              const isHidden = step.isVisible === false;

              return (
                <div
                  key={step.id}
                  draggable={!isLocked}
                  onDragStart={() => handleDragStart(step.id, index)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(step.id)}
                >
                  <OnboardingStepItem
                    step={{
                      ...step,
                      step: index + 1,
                    }}
                    index={index}
                    selected={step.id === selectedStepId}
                    disabled={isHidden}
                    isHidden={isHidden}
                    isLocked={isLocked}
                    onClick={() => handleSelectStep(step.id)}
                    onToggleVisible={() => toggleStepVisibility(step.id)}
                  />
                </div>
              );
            })}
          </div>
        </aside>

        <main className="relative overflow-auto bg-[#090E19] px-8 py-5">
          <div className="mb-4 flex items-center gap-2 text-xs text-neutral-100/50">
            <span className="h-2 w-2 rounded-full bg-green-500" />
            Live Preview
          </div>

          <OnboardingMobilePreview
            step={selectedStep}
            selectedFieldId={selectedFieldId}
            onSelectField={setSelectedFieldId}
            primaryColor={primaryColor}
            logoUrl={onboardingDetails?.logoUrl}
          />
        </main>

        <aside className="border-l border-brand-26 bg-brand-27">
          <OnboardingPropertiesPanel
            step={selectedStep}
            field={selectedField}
            onChange={(patch) => updateField(selectedFieldId, patch)}
            primaryColor={primaryColor}
          />
        </aside>
      </div>
      {/* <CustomSuccessModal
        open={showSaveConfirm}
        onClose={() => setShowSaveConfirm(false)}
        onConfirm={onPublish}
        image={isRepublish ? republishIcon : publishIcon}
        imageClassName={isRepublish ? "h-24 w-24" : "h-52 w-68"}
        title={
          isRepublish
            ? "Save Updated Onboarding?"
            : "Save Onboarding Experience?"
        }
        description={
          isRepublish
            ? "You have made changes to the onboarding experience. Saving now will replace the current live version with the latest changes."
            : "You are about to save this onboarding experience. Once saved, users will see these onboarding screens after sign in."
        }
        helperText={
          isRepublish
            ? "Users will see the updated onboarding screens after saving."
            : "Please review the content, media and screen order before Saving."
        }
        cancelText={isRepublish ? "Go Back" : "Cancel"}
        confirmText={isRepublish ? "Save" : "Save"}
      /> */}

      <CustomSuccessModal
        open={showSaveConfirm}
        onClose={() => setShowSaveConfirm(false)}
        onConfirm={handleConfirmSave}
        title="Save Updated Onboarding?"
        description="You have made changes to the onboarding experience. Saving now will replace the current live version with the latest changes."
        helperText="Please review the content, media and screen order before saving."
        cancelText="Cancel"
        confirmText={isUpdatingOnboarding ? "Saving..." : "Save"}
      />

      {showExitConfirm && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/70">
          <div className="w-[580px] rounded-lg border border-[#00D3F2]/40 bg-[#132033] px-8 py-8 text-center shadow-2xl">
            <div className="mx-auto mb-5 flex h-10 w-10 items-center justify-center rounded-full text-brand-116">
              <TriangleAlert className="h-12 w-12 fill-red-800" />
            </div>

            <h2 className="mb-3 text-xl font-bold text-[#67D8FF]">
              Exit without saving?
            </h2>

            <p className="mx-auto mb-10  text-sm leading-relaxed text-neutral-100">
              You have unsaved changes. If you exit now, your recent edits will
              be lost.
            </p>

            <div className="flex justify-center gap-3 mb-6">
              <Button
                type="button"
                variant="neutral"
                onClick={() => setShowExitConfirm(false)}
                className="h-9 border-[#607A9B] bg-transparent px-6 text-xs text-neutral-100 hover:bg-white/5"
              >
                Go Back
              </Button>

              <Button
                type="button"
                onClick={() => {
                  setShowExitConfirm(false);
                  navigate(-1);
                }}
                className="h-9 bg-[#168FE3] px-6 text-xs font-bold text-white hover:bg-[#0B7DCC]"
              >
                Exit without Saving
              </Button>
            </div>
          </div>
        </div>
      )}

      <AlertMsg
        label={alertMsg.label}
        msg={alertMsg.msg}
        open={alertMsg.open}
        onClose={handleAlertClose}
        confirmAction={handleAlertClose}
        isLoading={false}
        isError={alertMsg.isError}
        canClose={alertMsg.canClose}
        confirmationBtnText={alertMsg.btnText}
      />
    </div>
  );
}

const OnboardingStepItem = ({
  step,
  selected,
  disabled,
  isHidden,
  isLocked,
  onClick,
  onToggleVisible,
  index,
}) => {
  const isLockedToggle = isToggleLockedScreen(step);
  const hideEyeButton = isLocked;
  return (
    <div
      className={`w-full rounded-lg border px-3 py-3 text-left transition ${
        isHidden ? "opacity-75" : ""
      } ${
        selected && !isHidden
          ? "border-brand-28 bg-[#10364A]"
          : "border-[#232B40] bg-[#181F32] hover:border-[#315A78]"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="">
          {isLocked ? (
            <LockKeyhole className="h-4 w-4 text-white" strokeWidth={2} />
          ) : (
            <GripVertical
              className="h-4 w-4 cursor-grab text-white"
              title="Drag to reorder"
              strokeWidth={2}
            />
          )}
        </div>

        <button
          type="button"
          disabled={disabled}
          onClick={onClick}
          className="flex flex-1 gap-3 text-left disabled:cursor-not-allowed"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#101827]">
            <Zap className="h-4 w-4 text-brand-23" />
          </div>

          <div className="min-w-0">
            <div className="mb-1 flex items-center gap-2">
              <span className="text-xs text-neutral-100/45">
                Step {step.step}
              </span>

              <span className="rounded bg-brand-25 px-1.5 py-0.5 text-[10px] text-brand-24">
                {step.type}
              </span>
            </div>

            <p className="truncate text-xs font-bold text-neutral-100">
              {step.title}
            </p>
          </div>
        </button>

        {/* <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onToggleVisible();
          }}
          className={`flex h-7 w-7 items-center justify-center rounded-md transition`}
          title={isHidden ? "Enable screen" : "Disable screen"}
        >
          {isHidden ? (
            <EyeOff className="h-4 w-4 text-white" strokeWidth={2} />
          ) : (
            <Eye className="h-4 w-4 text-white" strokeWidth={3} />
          )}
        </button> */}
        {/* {!hideEyeButton && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onToggleVisible();
            }}
            className="flex h-7 w-7 items-center justify-center rounded-md transition"
            title={isHidden ? "Enable screen" : "Disable screen"}
          >
            {isHidden ? (
              <EyeOff className="h-4 w-4 text-white" strokeWidth={2} />
            ) : (
              <Eye className="h-4 w-4 text-white" strokeWidth={3} />
            )}
          </button>
        )} */}
        {!hideEyeButton && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onToggleVisible();
            }}
            className="flex h-7 w-7 items-center justify-center rounded-md transition"
            title={isHidden ? "Enable screen" : "Disable screen"}
          >
            {isHidden ? (
              <EyeOff className="h-4 w-4 text-white" strokeWidth={2} />
            ) : (
              <Eye className="h-4 w-4 text-white" strokeWidth={3} />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const OnboardingMobilePreview = ({
  step,
  selectedFieldId,
  onSelectField,
  primaryColor,
  logoUrl,
}) => {
  if (!step) {
    return (
      <div className="flex justify-center pt-10 text-sm text-neutral-100/50 break-words">
        No Data Available
      </div>
    );
  }

  if (step.id === "splash_screen") {
    return (
      <MobileFrame widthClass="w-[360px]">
        <SplashPreview
          step={step}
          selectedFieldId={selectedFieldId}
          onSelectField={onSelectField}
          logoUrl={logoUrl}
          primaryColor={primaryColor}
        />
      </MobileFrame>
    );
  }

  if (step.id === "welcome_intro") {
    return (
      <MobileFrame widthClass="w-[360px]">
        <WelcomePreview
          step={step}
          selectedFieldId={selectedFieldId}
          onSelectField={onSelectField}
          primaryColor={primaryColor}
        />
      </MobileFrame>
    );
  }

  if (step.id === "getting_started") {
    return (
      <MobileFrame widthClass="w-[360px]">
        <KickoffPreview
          step={step}
          selectedFieldId={selectedFieldId}
          onSelectField={onSelectField}
          primaryColor={primaryColor}
        />
      </MobileFrame>
    );
  }

  if (step.id === "home_screen") {
    return (
      <div className="flex justify-center pt-6 pb-10">
        <div className="w-[360px] rounded-[28px] border-[6px] border-[#344768] bg-[#07070B] p-2">
          <HomeScreenPreview
            step={step}
            selectedFieldId={selectedFieldId}
            onSelectField={onSelectField}
          />
        </div>
      </div>
    );
  }

  return (
    <MobileFrame widthClass="w-[360px]">
      <DynamicTextPreview
        step={step}
        selectedFieldId={selectedFieldId}
        onSelectField={onSelectField}
      />
    </MobileFrame>
  );
};

const MobileFrame = ({ children, widthClass = "w-[360px]" }) => {
  return (
    <div className="flex justify-center pt-6 pb-10">
      <div
        className={`${widthClass} rounded-[28px] border-[8px] border-[#344768] bg-[#07070B] px-4 py-10`}
      >
        {children}
      </div>
    </div>
  );
};

const SplashPreview = ({
  step,
  selectedFieldId,
  onSelectField,
  logoUrl,
  primaryColor,
}) => {
  const tagline =
    getField(step, "splash_title") || getField(step, "splashTagline");

  const loadingText =
    getField(step, "splash_loading_text") ||
    getField(step, "splashLoadingText");

  return (
    <div className="flex min-h-[560px] flex-col items-center justify-center">
      <div className="mb-10 text-center">
        <div className="flex justify-center">
          <img
            src={logoUrl || Theme_logo || "/placeholder.svg"}
            className="h-20"
            alt="Simstac Systems Logo"
          />
        </div>
      </div>

      {tagline && (
        <EditablePreviewBox
          selected={selectedFieldId === tagline.id}
          onClick={() => onSelectField(tagline.id)}
          className="px-4 py-3 text-center text-sm tracking-[5px]"
          style={{
            color: tagline.color,
            fontSize: tagline.fontSize,
          }}
        >
          {tagline.value}
        </EditablePreviewBox>
      )}

      <div className="mt-24 w-full">
        {/* <div className="mx-auto mb-3 h-1 w-28 rounded bg-gradient-to-r from-brand-28 to-[#7C2AFF]" /> */}
        <div
          className="mx-auto mb-3 h-1 w-28 rounded"
          style={{ backgroundColor: primaryColor }}
        />

        {loadingText && (
          <EditablePreviewBox
            selected={selectedFieldId === loadingText.id}
            onClick={() => onSelectField(loadingText.id)}
            className="py-2 text-center text-xs"
            style={{
              color: loadingText.color,
              fontSize: loadingText.fontSize,
            }}
          >
            {loadingText.value}
          </EditablePreviewBox>
        )}
      </div>
    </div>
  );
};

const WelcomePreview = ({
  step,
  selectedFieldId,
  onSelectField,
  primaryColor,
}) => {
  const fields = [...(step.editableFields || [])].sort(
    (a, b) => a.priorityOrder - b.priorityOrder,
  );

  return (
    <div className="w-full min-h-[560px]">
      {fields.map((item) => {
        if (item.kind === "button") {
          return (
            <EditablePreviewBox
              key={item.id}
              selected={selectedFieldId === item.id}
              onClick={() => onSelectField(item.id)}
              className="mx-auto mt-6 inline-flex min-w-[140px] items-center justify-center rounded-xl px-5 py-4 text-base font-bold"
              style={{
                backgroundColor:
                  item.buttonBGColor || primaryColor || "#00D3F2",
                color: item.color || "#000000",
                fontSize: item.fontSize,
              }}
            >
              {item.value}
            </EditablePreviewBox>
          );
        }

        const isTitle =
          item.label?.toLowerCase().includes("title") ||
          item.label?.toLowerCase().includes("heading");

        const isTag =
          item.label?.toLowerCase().includes("tag") ||
          item.label?.toLowerCase().includes("label");

        const isHighlight = item.label?.toLowerCase().includes("highlight");

        return (
          <EditablePreviewBox
            key={item.id}
            selected={selectedFieldId === item.id}
            onClick={() => onSelectField(item.id)}
            className={`mb-3 px-3 py-3 text-center whitespace-pre-line ${
              isTitle
                ? "text-[22px] font-bold leading-[1.2]"
                : isTag
                  ? "text-[11px] tracking-[4px]"
                  : isHighlight
                    ? "text-[13px] font-semibold"
                    : "text-[13px] leading-[1.7]"
            }`}
            style={{
              color: getTextColor(item, primaryColor),
              fontSize: item.fontSize,
            }}
          >
            {item.value}
          </EditablePreviewBox>
        );
      })}
    </div>
  );
};

const KickoffPreview = ({
  step,
  selectedFieldId,
  onSelectField,
  primaryColor,
}) => {
  const small = getField(step, "getting_started_label");
  const title = getField(step, "getting_started_heading");
  const para1 = getField(step, "getting_started_description_1");
  const para2 = getField(step, "getting_started_description_2");
  const button = getField(step, "getting_started_button_text");

  return (
    <div className="w-full min-h-[560px]">
      {small && (
        <EditablePreviewBox
          selected={selectedFieldId === small.id}
          onClick={() => onSelectField(small.id)}
          className="mb-3 px-3 py-3 text-center text-[11px] tracking-[4px] break-words"
          style={{ color: small.color, fontSize: small.fontSize }}
        >
          {small.value}
        </EditablePreviewBox>
      )}

      {title && (
        <EditablePreviewBox
          selected={selectedFieldId === title.id}
          onClick={() => onSelectField(title.id)}
          className="mb-3 px-3 py-3 text-center text-[22px] leading-[1.25] font-bold break-words"
          style={{
            color: getTextColor(title, primaryColor),
            fontSize: title.fontSize,
          }}
        >
          {title.value}
        </EditablePreviewBox>
      )}

      {para1 && (
        <EditablePreviewBox
          selected={selectedFieldId === para1.id}
          onClick={() => onSelectField(para1.id)}
          className="mb-3 px-3 py-4 text-center text-[13px] leading-[1.7] break-words"
          style={{ color: para1.color, fontSize: para1.fontSize }}
        >
          {para1.value}
        </EditablePreviewBox>
      )}

      {para2 && (
        <EditablePreviewBox
          selected={selectedFieldId === para2.id}
          onClick={() => onSelectField(para2.id)}
          className="mb-16 px-3 py-4 text-center text-[13px] leading-[1.7] break-words"
          style={{ color: para2.color, fontSize: para2.fontSize }}
        >
          {para2.value}
        </EditablePreviewBox>
      )}

      {button && (
        <EditablePreviewBox
          selected={selectedFieldId === button.id}
          onClick={() => onSelectField(button.id)}
          className="mx-auto inline-flex min-w-[160px] items-center justify-center rounded-xl px-5 py-4 text-base font-bold break-words"
          style={{
            backgroundColor: button.buttonBGColor || primaryColor || "#00D3F2",
            color: button.color || "#000000",
            fontSize: button.fontSize,
          }}
        >
          {button.value}
        </EditablePreviewBox>
      )}
    </div>
  );
};

const HomeScreenPreview = ({ step, selectedFieldId, onSelectField }) => {
  const description = getField(step, "home_description");

  return (
    <div className="relative min-h-[560px] w-full overflow-hidden rounded-[22px] bg-[#070A12] px-4 py-4">
      <div className="mb-5 flex items-center justify-between text-[10px] text-white">
        <span></span>
        {/* <span>9:41</span> */}

        <div className="flex items-center gap-1">
          <span className="h-2 w-3 rounded-sm border border-white" />
          <span className="h-2 w-3 rounded-sm border border-white" />
          <span className="h-2 w-4 rounded-sm bg-white" />
        </div>
      </div>

      <div className="mb-2 flex items-start justify-between">
        <p className="px-0 py-1 text-left text-[16px] font-medium text-white">
          Welcome Alex
        </p>

        <div className="relative flex h-9 w-9 items-center justify-center rounded-full bg-[#0A8CA0]">
          <span className="text-[13px] font-bold text-white">A</span>
          <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-[#070A12] bg-[#46FF6A]" />
        </div>
      </div>

      {description && (
        <EditablePreviewBox
          selected={selectedFieldId === description.id}
          onClick={() => onSelectField(description.id)}
          className="mb-3 border-[3px] border-[#DFFF00] px-2 py-2 text-left text-[10px] leading-[1.45] break-words"
          style={{
            color: description.color || "#FFFFFF",
            fontSize: description.fontSize || 14,
          }}
        >
          {description.value}
        </EditablePreviewBox>
      )}

      <div className="space-y-2">
        {[1, 2, 3].map((item) => (
          <div
            key={item}
            className="rounded-lg border border-white/10 bg-[#171717] px-3 py-3"
          >
            <div className="mb-5 flex items-start justify-between">
              <div className="h-7 w-7 rounded bg-[#666666]" />
              <div className="h-5 w-10 rounded bg-[#666666]" />
            </div>

            <div className="space-y-2">
              <div className="h-2 w-[62%] rounded-full bg-[#666666]" />
              <div className="h-2 w-full rounded-full bg-[#666666]" />
              <div className="h-2 w-full rounded-full bg-[#666666]" />
              <div className="h-2 w-[74%] rounded-full bg-[#666666]" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const DynamicTextPreview = ({ step, selectedFieldId, onSelectField }) => {
  const fields = [...(step.editableFields || [])].sort(
    (a, b) => a.priorityOrder - b.priorityOrder,
  );

  return (
    <div className="w-full min-h-[560px]">
      {fields.map((field) => (
        <EditablePreviewBox
          key={field.id}
          selected={selectedFieldId === field.id}
          onClick={() => onSelectField(field.id)}
          className="mb-3 px-3 py-3 text-center text-[13px] leading-[1.7] break-words"
          style={{
            color: field.color,
            fontSize: field.fontSize,
          }}
        >
          {field.value}
        </EditablePreviewBox>
      ))}
    </div>
  );
};

const EditablePreviewBox = ({
  selected,
  onClick,
  className = "",
  style = {},
  children,
}) => {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className={`block w-full whitespace-pre-line border break-words ${
        selected ? "border-brand-23" : "border-dashed border-white/25"
      } ${className}`}
      style={style}
    >
      {children}
    </button>
  );
};

const OnboardingPropertiesPanel = ({ step, field, onChange, primaryColor }) => {
  if (!step || !field) {
    return (
      <div className="p-5 text-sm text-neutral-100/50">
        Select a field to edit.
      </div>
    );
  }
  const isButton = field.kind === "button";
  const maxLength = getMaxLength(field);
  const buttonBgValue = getButtonBgColor(field, primaryColor);
  return (
    <div className="h-full overflow-y-auto p-5">
      <div className="border-b border-brand-26 px-5 py-5">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-yellow-400" />
          <p className="text-sm font-semibold">Properties</p>
        </div>

        <p className="mt-2 text-sm text-neutral-100/60">{step.title}</p>
      </div>

      <div className="mb-4 rounded-lg bg-[#101827] p-4">
        <p className="mb-3 text-xs font-bold uppercase text-neutral-100/70">
          Content
        </p>
        <div className="mb-3 flex items-center gap-2 text-xs font-bold text-neutral-100/70">
          {field.label}
        </div>

        <textarea
          value={field.value}
          maxLength={maxLength}
          onChange={(event) =>
            onChange({
              value: event.target.value.slice(0, maxLength),
            })
          }
          className="min-h-[84px] w-full rounded-md border border-transparent bg-[#1D2438] px-3 py-3 text-sm text-neutral-100 outline-none focus:border-brabg-brand-28"
        />
        <p className="mt-2 text-right text-xs text-neutral-100/45">
          {field.value?.length || 0}/{maxLength}
        </p>
      </div>

      <div className="space-y-4 rounded-lg bg-[#101827] p-4">
        <div>
          <p className="mb-3 text-xs font-bold uppercase text-neutral-100/70">
            Styling
          </p>

          <label className="mb-2 block text-xs text-neutral-100/70">
            Text Color
          </label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={
                field?.usesPrimaryColor === true
                  ? primaryColor || field.color || "#FFFFFF"
                  : field.color || "#FFFFFF"
              }
              onChange={(event) =>
                onChange({
                  color: event.target.value,
                  usesPrimaryColor: false,
                })
              }
              className="h-9 w-10 cursor-pointer rounded border-0 bg-transparent p-0"
            />
            <Input
              value={
                field?.usesPrimaryColor === true
                  ? primaryColor || field.color || "#FFFFFF"
                  : field.color || "#FFFFFF"
              }
              onChange={(event) =>
                onChange({
                  targetStepId: step.id,
                  targetFieldId: field.id,
                  color: event.target.value,
                  usesPrimaryColor: false,
                })
              }
              className="h-9 border-none bg-[#1D2438] text-neutral-100"
            />
          </div>
          {isButton && (
            <div className="mt-5">
              <label className="mb-2 block text-xs text-neutral-100/70">
                Button Color
              </label>

              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={buttonBgValue}
                  onChange={(event) => {
                    onChange({
                      targetStepId: step.id,
                      targetFieldId: field.id,
                      buttonBGColor: event.target.value,
                    });
                  }}
                  className="h-9 w-10 cursor-pointer rounded border-0 bg-transparent p-0"
                />

                <Input
                  value={buttonBgValue}
                  onChange={(event) => {
                    const nextColor = event.target.value;

                    onChange({
                      targetStepId: step.id,
                      targetFieldId: field.id,
                      buttonBGColor: nextColor,
                    });
                  }}
                  className="h-9 border-none bg-[#1D2438] text-neutral-100"
                />
              </div>

              <p className="mt-2 text-xs text-neutral-100/45">
                Button text max 20 characters
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
